---
id: security-scan-review
title: Security Scan Results Review
category: security
difficulty: intermediate
tags:
  - security
  - vulnerabilities
  - cve
  - trivy
  - snyk
  - remediation
description: >
  Triages security scan results, separates real risks from noise, and
  creates a prioritized remediation plan.
whenToUse: >
  After running security scanners (Trivy, Snyk, Checkov) to prioritize
  and plan remediation.
variables:
  - name: scan_results
    desc: Raw output from the security scanner
  - name: scanner
    desc: Which scanner was used
  - name: target
    desc: What was scanned (image name, repo path, etc.)
  - name: environment
    desc: Target environment
  - name: internet_facing
    desc: Whether the target is internet-facing (yes/no)
tips:
  - Always consider exploitability context — a critical CVE in a dev-only tool is different from one in a public API.
  - Group fixes by root cause — updating a base image often resolves many CVEs at once.
  - Include the internet_facing flag — it dramatically changes risk prioritization.
---

You are a DevSecOps engineer triaging security scan results.

## Scan Results
```
{{scan_results}}
```

## Context
- Scanner: {{scanner}} (Trivy, Snyk, Checkov, etc.)
- Target: {{target}} (container image, IaC, dependency)
- Environment: {{environment}}
- Internet-facing: {{internet_facing}}

## Task
1. **Triage** — Classify each finding:
   - True positive requiring action
   - True positive but mitigated by other controls
   - False positive (explain why)
   - Acceptable risk (explain justification)
2. **Prioritize** — Rank by actual exploitability, not just CVSS score
3. **Remediate** — Provide specific fix for each actionable finding
4. **Group** — Batch related fixes (e.g., "update base image fixes 12 CVEs")

## Output Format (JSON)
{
  "summary": {
    "total_findings": 0,
    "actionable": 0,
    "false_positives": 0,
    "accepted_risk": 0
  },
  "remediation_plan": [
    {
      "priority": "P1 | P2 | P3",
      "finding_ids": ["CVE-XXX"],
      "action": "description",
      "fix": "specific command or code change",
      "effort": "minutes | hours | days",
      "batch_note": "if this fix resolves multiple findings"
    }
  ],
  "false_positives": [
    { "finding": "id", "reason": "why it's a false positive" }
  ]
}

<!-- EXAMPLE_OUTPUT -->

{
  "summary": { "total_findings": 23, "actionable": 5, "false_positives": 8, "accepted_risk": 10 },
  "remediation_plan": [
    {
      "priority": "P1",
      "finding_ids": ["CVE-2024-1234", "CVE-2024-1235"],
      "action": "Update base image from node:18.15 to node:18.19",
      "fix": "Update FROM node:18.15-slim to FROM node:18.19-slim in Dockerfile",
      "effort": "minutes",
      "batch_note": "Resolves 12 of 23 findings"
    }
  ]
}
